/**
 * BoundingBox Gizmo TransformNode Support Service
 *
 * The Inspector's built-in gizmo manager skips the BoundingBoxGizmo for non-Mesh
 * entities (it gates on `resolvedEntity instanceof AbstractMesh`). Since our
 * SceneObjects expose a TransformNode, the bounding box gizmo never appears.
 *
 * This service works around that by managing its own BoundingBoxGizmo instance.
 * When gizmo mode is "boundingBox" and the selected entity is a TransformNode
 * (not an AbstractMesh), we create and attach the gizmo ourselves.
 */

import type { ServiceDefinition, IGizmoService, ISelectionService } from '@babylonjs/inspector';
import { GizmoServiceIdentity, SelectionServiceIdentity } from '@babylonjs/inspector';
import { BoundingBoxGizmo } from '@babylonjs/core/Gizmos/boundingBoxGizmo';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { hookGizmoInstance } from './undo-gizmo-service';
import { isScaleBoosting, SCALE_BOOST } from './gizmo-snap-service';
import { getUndoManager, createSmartScaleRig } from 'trace-viewer/editor';
import type { SmartScaleRig, UndoCommand } from 'trace-viewer/editor';
import { Layout } from 'trace-viewer/behaviors';

// ============================================================================
// Scale boost for BoundingBoxGizmo
// ============================================================================

/** Snapshot of both position and scaling so we can amplify both deltas. */
interface BoxBoostState {
    prevPos: [number, number, number];
    prevScale: [number, number, number];
}


export const BoundingBoxGizmoServiceDefinition: ServiceDefinition<[], [IGizmoService, ISelectionService]> = {
    friendlyName: 'BoundingBox Gizmo TransformNode Support',
    consumes: [GizmoServiceIdentity, SelectionServiceIdentity],

    factory: (gizmoService: IGizmoService, selectionService: ISelectionService) => {
        let gizmo: BoundingBoxGizmo | null = null;
        let boostState: BoxBoostState | null = null;
        let scaleBoostObservers: { remove(): void }[] = [];
        // The engine owns the whole gesture for a smart component: which
        // sizes its morphs can still realize, the frame the drag has to be
        // read in, keeping the anchored face planted, and the one undo entry
        // that covers both the size and the move that anchoring causes.
        //
        // This service supplies only what it alone knows — a box to paint, and
        // WHERE TO PUT IT: the gizmo attaches to `rig.proxy`, not to the
        // object. Driving the object's own node is what made a drag open both
        // sides, break under rotation, and undo to the wrong place.
        let smartScale: SmartScaleRig | null = null;
        const rig = (): SmartScaleRig => {
            smartScale ??= createSmartScaleRig(
                getUndoManager().traceScene!.getBabylonScene(),
                () => gizmo,
                // The gizmo does not always see the release: let go outside the
                // canvas and Babylon never gets a POINTERUP to forward, so its
                // drag-end never fires. The rig finishes the gesture itself and
                // hands the entry back here, so BOTH paths land in one place.
                { onGestureEnd: finishGesture },
            );
            return smartScale;
        };

        /** One place a gesture finishes, however the release arrived. */
        function finishGesture(command: UndoCommand | null): void {
            if (command) getUndoManager().push(command);
            // The morphs changed the geometry, so the box has to re-fit or the
            // handles sit where the component used to end.
            smartScale?.refresh();
        }

        function hookSmartScale(g: BoundingBoxGizmo): void {
            const onStart = g.onDragStartObservable.add(() => { rig().begin(); });
            const onDrag = g.onScaleBoxDragObservable.add(() => { rig().update(); });
            const onEnd = g.onScaleBoxDragEndObservable.add(() => { finishGesture(rig().end()); });
            if (onStart) scaleBoostObservers.push(onStart);
            if (onDrag) scaleBoostObservers.push(onDrag);
            if (onEnd) scaleBoostObservers.push(onEnd);
        }

        function hookScaleBoost(g: BoundingBoxGizmo): void {
            const onStart = g.onDragStartObservable.add(() => {
                const node = g.attachedMesh;
                if (!node) return;
                boostState = {
                    prevPos: [node.position.x, node.position.y, node.position.z],
                    prevScale: [node.scaling.x, node.scaling.y, node.scaling.z],
                };
            });

            const onDrag = g.onScaleBoxDragObservable.add(() => {
                if (!isScaleBoosting() || !boostState) return;
                const node = g.attachedMesh;
                if (!node) return;
                // A smart component's drag is a SIZE request the engine
                // realizes on its meshes; amplifying node scaling on top would
                // apply the gesture twice and leave a scale the size does not
                // account for.
                if (smartScale?.active) return;

                // The gizmo already applied its own delta (scale + position offset).
                // We amplify that delta by (SCALE_BOOST - 1) so the total becomes SCALE_BOOST×.
                const extra = SCALE_BOOST - 1;

                const dsx = node.scaling.x - boostState.prevScale[0];
                const dsy = node.scaling.y - boostState.prevScale[1];
                const dsz = node.scaling.z - boostState.prevScale[2];
                node.scaling.x += dsx * extra;
                node.scaling.y += dsy * extra;
                node.scaling.z += dsz * extra;

                const dpx = node.position.x - boostState.prevPos[0];
                const dpy = node.position.y - boostState.prevPos[1];
                const dpz = node.position.z - boostState.prevPos[2];
                node.position.x += dpx * extra;
                node.position.y += dpy * extra;
                node.position.z += dpz * extra;

                node.computeWorldMatrix(true);

                boostState.prevPos = [node.position.x, node.position.y, node.position.z];
                boostState.prevScale = [node.scaling.x, node.scaling.y, node.scaling.z];
            });

            const onEnd = g.onScaleBoxDragEndObservable.add(() => {
                boostState = null;
            });

            if (onStart) scaleBoostObservers.push(onStart);
            if (onDrag) scaleBoostObservers.push(onDrag);
            if (onEnd) scaleBoostObservers.push(onEnd);
        }

        function sync(): void {
            const mode = gizmoService.gizmoMode;
            const entity = selectionService.selectedEntity;

            // Only act when mode is boundingBox AND entity is a TransformNode but NOT an AbstractMesh
            // (AbstractMesh case is handled by the Inspector's built-in gizmo manager)
            // Skip if the object has a Layout behavior — layout handles take over instead.
            const hasLayout = (() => {
                if (!entity || !(entity instanceof TransformNode)) return false;
                const so = getUndoManager().traceScene?.findSceneObjectByTransform(entity);
                return so ? !!so.getBehavior(Layout) : false;
            })();

            // Never in play mode — editor gizmos must not move runtime content.
            const inPlayMode = getUndoManager().traceScene?.isPlayMode() ?? false;

            const needsCustomGizmo =
                mode === 'boundingBox' &&
                !inPlayMode &&
                entity instanceof TransformNode &&
                !(entity instanceof AbstractMesh) &&
                !hasLayout;

            if (needsCustomGizmo) {
                if (!gizmo) {
                    gizmo = new BoundingBoxGizmo(
                        Color3.FromHexString('#0984e3'),
                        UtilityLayerRenderer.DefaultKeepDepthUtilityLayer,
                    );
                    gizmo.fixedDragMeshScreenSize = true;
                    hookGizmoInstance(gizmo);
                    hookScaleBoost(gizmo);
                    hookSmartScale(gizmo);
                }
                // BoundingBoxGizmo needs attachedMesh, not attachedNode.
                // TransformNode has all APIs that BoundingBoxGizmo uses, so the cast is safe.
                // A smart component's gizmo holds the engine's proxy, which
                // is built in the component's OWN frame — that is what makes
                // the handles lie on its faces when it is rotated, and what
                // lets a one-sided drag stay one-sided. Anything else keeps
                // the ordinary attach. `attach` also paints: purple before any
                // drag, because the colour states what the NEXT drag will do.
                const object = getUndoManager().traceScene?.findSceneObjectByTransform(entity) ?? null;
                const smart = rig().attach(object);
                gizmo.attachedMesh = (smart && rig().proxy
                    ? rig().proxy
                    : entity) as unknown as AbstractMesh;
            } else {
                detach();
            }
        }

        function detach(): void {
            if (gizmo) {
                gizmo.attachedMesh = null;
            }
        }

        const modeObserver = gizmoService.onGizmoModeChanged.add(sync);
        const selectionObserver = selectionService.onSelectedEntityChanged.add(sync);

        // Re-sync on play-mode changes so the gizmo detaches when the scene
        // starts running and re-attaches on stop. Listens on `window` rather
        // than the scene's event system: the event bubbles composed off the
        // viewer element, and the service can be constructed before the scene
        // exists — a once-captured event system would then be null and the
        // detach trigger would never arrive.
        const playModeHandler = () => sync();
        window.addEventListener('trace-viewer:playmode-changed', playModeHandler);

        // Initial sync in case inspector already has a selection
        sync();

        return {
            dispose: () => {
                modeObserver?.remove();
                selectionObserver?.remove();
                window.removeEventListener('trace-viewer:playmode-changed', playModeHandler);
                for (const obs of scaleBoostObservers) obs.remove();
                scaleBoostObservers = [];
                boostState = null;
                if (gizmo) {
                    gizmo.dispose();
                    gizmo = null;
                }
            },
        };
    },
};
